import React, {
    useState,
    useRef,
    useEffect,
    useMemo,
    useCallback
} from "react";
import { MDBCheckbox, MDBIcon, MDBBadge } from "mdb-react-ui-kit";
import { AnimatePresence, motion } from "framer-motion";


const DiscountRow = React.memo(({ selectData, value, onChange }) => {
    const [expandedPlans, setExpandedPlans] = useState([]);
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const dropdownRef = useRef(null);

    // Organize plans hierarchically
    const organizedPlans = useMemo(() => {
        const basePlansMap = {};

        selectData.forEach(plan => {
            const planName = plan.text;

            if (planName.includes(" - ")) {
                // Subplan case
                const [basePlanName] = planName.split(" - ");
                const cleanBasePlanName = basePlanName
                    .replace(/ \(Plan\)$/, "")
                    .replace(/ \(Addon\)$/, "");

                if (!basePlansMap[cleanBasePlanName]) {
                    basePlansMap[cleanBasePlanName] = {
                        basePlan: {
                            text: cleanBasePlanName,
                            value: cleanBasePlanName,
                            hasVariations: true
                        },
                        subplans: []
                    };
                }

                basePlansMap[cleanBasePlanName].subplans.push({
                    ...plan,
                    label: plan.text
                });
            } else {
                const cleanPlanName = planName.replace(/ \(Plan\)$/, "").replace(/ \(Addon\)$/, "");
                if (!basePlansMap[cleanPlanName]) {
                    basePlansMap[cleanPlanName] = {
                        basePlan: {
                            ...plan,
                            text: cleanPlanName,
                            label: plan.text,
                            hasVariations: false
                        },
                        subplans: []
                    };
                }
            }
        });

        return Object.values(basePlansMap);
    }, [selectData]);

    const togglePlan = (planName, e) => {
        if (e) e.stopPropagation();
        setExpandedPlans(prev =>
            prev.includes(planName)
                ? prev.filter(name => name !== planName)
                : [...prev, planName]
        );
    };

    const handlePlanSelection = (planValue, isSelected, e) => {
        if (e) e.stopPropagation();
        const newValue = isSelected
            ? [...value, planValue]
            : value.filter(val => val !== planValue);
        onChange(newValue);
    };

    const handleBasePlanSelection = (basePlan, subplans, isSelected, e) => {
        if (e) e.stopPropagation();
        let newValue = [...value];

        if (isSelected) {
            const subplanValues = subplans.map(sp => sp.value);
            newValue = [...new Set([...newValue, ...subplanValues])];

            if (!expandedPlans.includes(basePlan.text)) {
                setExpandedPlans([...expandedPlans, basePlan.text]);
            }
        } else {
            const subplanValues = subplans.map(sp => sp.value);
            newValue = newValue.filter(val => !subplanValues.includes(val));
        }

        onChange(newValue);
    };

    const isBasePlanSelected = (subplans) => {
        if (subplans.length === 0) return false;
        return subplans.every(sp => value.includes(sp.value));
    };

    const isBasePlanIndeterminate = (subplans) => {
        if (subplans.length === 0) return false;
        const selectedCount = subplans.filter(sp => value.includes(sp.value)).length;
        return selectedCount > 0 && selectedCount < subplans.length;
    };

    const handleDropdownToggle = (e) => {
        e.stopPropagation();
        setIsDropdownOpen(!isDropdownOpen);
    };

    const handleClickOutside = useCallback((e) => {
        if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
            setIsDropdownOpen(false);
        }
    }, []);

    useEffect(() => {
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [handleClickOutside]);

    const selectedCount = value.length;

    return (
        <div className="custom-plan-selector" ref={dropdownRef}>
            {/* Dropdown Toggle */}
            <div
                className="dropdown-toggle form-control multi-toggle"
                onClick={handleDropdownToggle}
                style={{
                    cursor: "pointer",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center"
                }}
            >
                <span>
                    {selectedCount === 0 ? "All Plans" : `${selectedCount} selected`}
                </span>
                <MDBIcon
                    fas
                    icon={isDropdownOpen ? "chevron-up" : "chevron-down"}
                />
            </div>

            {/* Dropdown Menu */}
            {isDropdownOpen && (
                <div className="custom-plan-dropdown">
                    {organizedPlans.map(({ basePlan, subplans }) => {
                        const hasSubplans = subplans.length > 0;
                        const isExpanded = expandedPlans.includes(basePlan.text);
                        const allSubplansSelected = isBasePlanSelected(subplans);
                        const someSubplansSelected = isBasePlanIndeterminate(subplans);

                        return (
                            <div key={basePlan.text} className="plan-item" style={{ padding: "8px", borderBottom: "1px solid #eee" }}>
                                {hasSubplans ? (
                                    <>
                                        {/* Base Plan with Variations */}
                                        <div
                                            className="base-plan-header"
                                            style={{
                                                display: "flex",
                                                justifyContent: "space-between",
                                                alignItems: "center",
                                                cursor: "pointer"
                                            }}
                                        >
                                            <div className="d-flex align-items-center flex-grow-1 justify-content-between">
                                                <div
                                                    className="d-flex align-items-center"
                                                    onClick={(e) => e.stopPropagation()}
                                                >
                                                    <MDBCheckbox
                                                        checked={allSubplansSelected}
                                                        indeterminate={someSubplansSelected}
                                                        onChange={(e) =>
                                                            handleBasePlanSelection(basePlan, subplans, e.target.checked, e)
                                                        }
                                                    />
                                                    <span className="base-plan-text ms-2">{basePlan.text}</span>
                                                </div>
                                                <MDBBadge light className="ms-2 me-2 varint-badge">
                                                    Variations
                                                </MDBBadge>
                                            </div>
                                            <MDBIcon
                                                fas
                                                icon={isExpanded ? "chevron-down" : "chevron-right"}
                                                onClick={(e) => togglePlan(basePlan.text, e)}
                                                style={{ cursor: "pointer" }}
                                            />
                                        </div>

                                        {/* Subplans with Animation */}
                                        <AnimatePresence initial={false}>
                                            {isExpanded && (
                                                <motion.div
                                                    className="subplans-container"
                                                    style={{ marginLeft: "20px" }}
                                                    initial={{ height: 0, opacity: 0 }}
                                                    animate={{ height: "auto", opacity: 1 }}
                                                    exit={{ height: 0, opacity: 0 }}
                                                    transition={{ duration: 0.3, ease: "easeInOut" }}
                                                >
                                                    {subplans.map((subplan) => (
                                                        <div key={subplan.value} className="subplan-item" style={{ padding: "4px 0" }}>
                                                            <MDBCheckbox
                                                                checked={value.includes(subplan.value)}
                                                                onChange={(e) => handlePlanSelection(subplan.value, e.target.checked, e)}
                                                                label={subplan.label}
                                                            />
                                                        </div>
                                                    ))}
                                                </motion.div>
                                            )}
                                        </AnimatePresence>
                                    </>
                                ) : (
                                    <div className="standalone-plan multi-check-plan">
                                        <MDBCheckbox
                                            checked={value.includes(basePlan.value)}
                                            onChange={(e) => handlePlanSelection(basePlan.value, e.target.checked, e)}
                                            label={basePlan.label}
                                        />
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
});

export default DiscountRow;
